/**
 * TableSparkline Component
 *
 * A compact sparkline chart for displaying trend data in table cells.
 * Renders as SVG with auto-scaling Y-axis and trend-based coloring.
 *
 * Design:
 * - 60px wide x 20px tall (configurable)
 * - Green (#4caf50) when trending up (last > first)
 * - Red (#ff6b6b) when trending down (last < first)
 * - End dot highlights the most recent value
 * - Respects prefers-reduced-motion for accessibility
 *
 * @see docs/stories/DRE-CLIENT-V2-story-4.003.md
 */

import { useMemo } from 'react'

export interface TableSparklineProps {
  /** Array of numeric values to display as sparkline */
  data: number[]
  /** Width of the sparkline SVG (default: 60) */
  width?: number
  /** Height of the sparkline SVG (default: 20) */
  height?: number
  /** Optional className for additional styling */
  className?: string
}

// Color constants matching Monutrack design system
const COLOR_POSITIVE = '#4caf50' // Success green
const COLOR_NEGATIVE = '#ff6b6b' // Error red
const COLOR_NEUTRAL = '#666' // Neutral gray

/**
 * Calculate the trend color based on first and last values
 */
function getTrendColor(data: number[]): string {
  if (data.length < 2) return COLOR_NEUTRAL

  const first = data[0]
  const last = data[data.length - 1]

  if (last > first) return COLOR_POSITIVE
  if (last < first) return COLOR_NEGATIVE
  return COLOR_NEUTRAL
}

/**
 * Generate SVG polyline points from data array
 */
function generatePoints(
  data: number[],
  width: number,
  height: number,
  padding: number
): string {
  if (data.length === 0) return ''
  if (data.length === 1) {
    // Single point: render in center
    const x = width / 2
    const y = height / 2
    return `${x},${y}`
  }

  const minY = Math.min(...data)
  const maxY = Math.max(...data)
  const range = maxY - minY || 1 // Avoid division by zero for flat lines

  const effectiveWidth = width - padding * 2
  const effectiveHeight = height - padding * 2

  return data
    .map((value, index) => {
      const x = padding + (index / (data.length - 1)) * effectiveWidth
      const y = padding + effectiveHeight - ((value - minY) / range) * effectiveHeight
      return `${x.toFixed(2)},${y.toFixed(2)}`
    })
    .join(' ')
}

/**
 * Get the position of the last data point for the end dot
 */
function getLastPointPosition(
  data: number[],
  width: number,
  height: number,
  padding: number
): { x: number; y: number } | null {
  if (data.length === 0) return null

  if (data.length === 1) {
    return { x: width / 2, y: height / 2 }
  }

  const minY = Math.min(...data)
  const maxY = Math.max(...data)
  const range = maxY - minY || 1

  const effectiveWidth = width - padding * 2
  const effectiveHeight = height - padding * 2

  const lastValue = data[data.length - 1]
  const x = padding + effectiveWidth // Last position
  const y = padding + effectiveHeight - ((lastValue - minY) / range) * effectiveHeight

  return { x, y }
}

export function TableSparkline({
  data,
  width = 60,
  height = 20,
  className = '',
}: TableSparklineProps): JSX.Element | null {
  // Memoize calculations
  const padding = 2
  const strokeWidth = 1.5
  const dotRadius = 2.5

  const points = useMemo(
    () => generatePoints(data, width, height, padding),
    [data, width, height]
  )

  const lastPoint = useMemo(
    () => getLastPointPosition(data, width, height, padding),
    [data, width, height]
  )

  const color = useMemo(() => getTrendColor(data), [data])

  // Don't render anything for empty data
  if (data.length === 0) {
    return null
  }

  // For single point, just render a dot
  if (data.length === 1 && lastPoint) {
    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className={`motion-safe:transition-colors motion-safe:duration-150 ${className}`}
        role="img"
        aria-label="Sparkline with single data point"
      >
        <circle
          cx={lastPoint.x}
          cy={lastPoint.y}
          r={dotRadius}
          fill={color}
        />
      </svg>
    )
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={`motion-safe:transition-colors motion-safe:duration-150 ${className}`}
      role="img"
      aria-label={`Sparkline showing ${data.length} data points, trend is ${color === COLOR_POSITIVE ? 'positive' : color === COLOR_NEGATIVE ? 'negative' : 'neutral'}`}
    >
      {/* Trend line */}
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* End dot highlighting the most recent value */}
      {lastPoint && (
        <circle
          cx={lastPoint.x}
          cy={lastPoint.y}
          r={dotRadius}
          fill={color}
        />
      )}
    </svg>
  )
}

export default TableSparkline
